// Shared client-side validator for the Alloggiati Web guest record.
//
// Mirrors the fixed-width record rules enforced server-side in
// `backend/server.py::_guest_to_alloggiati_line` (Polizia di Stato spec, June
// 2020 — still current in 2026). The goal is to disable the submit button and
// surface per-field errors BEFORE the request hits the API, so both the guest
// (SelfCheckin.jsx) and the host (GuestsTab.jsx AddGuestDialog) get instant
// feedback.
//
// Every rule below is authoritative — do not relax it without also updating
// the server-side line builder, otherwise the file uploaded to Alloggiati Web
// will be rejected by the Polizia portal.

export const PROVINCE_CODES = [
  "AG","AL","AN","AO","AR","AP","AT","AV","BA","BT","BL","BN","BG","BI","BO","BZ","BS","BR","CA","CL",
  "CB","CI","CE","CT","CZ","CH","CO","CS","CR","KR","CN","EN","FM","FE","FI","FG","FC","FR","GE","GO",
  "GR","IM","IS","AQ","SP","LT","LE","LC","LI","LO","LU","MC","MN","MS","MT","VS","ME","MI","MO","MB",
  "NA","NO","NU","OG","OT","OR","PD","PA","PR","PV","PG","PU","PE","PC","PI","PT","PN","PZ","PO","RG",
  "RA","RC","RE","RI","RN","RM","RO","SA","SS","SV","SI","SR","SO","TA","TE","TR","TO","TP","TN","TV",
  "TS","UD","VA","VE","VB","VC","VR","VV","VI","VT",
];

const ITALY_COUNTRY_CODE = "100000100";
// Accepted "Tipo alloggiato" values from the Alloggiati Web spec.
const GUEST_TYPES = new Set(["16", "17", "18", "19", "20"]);
// Doc-number payload must fit in the 20-char field and only contain
// alphanumerics — spaces/hyphens are stripped server-side but we reject them
// upfront so the guest sees the issue immediately.
const DOC_NUMBER_RE = /^[A-Z0-9]{4,20}$/;

/**
 * Validate a single Alloggiati Web guest record.
 *
 * @param {object} f  — form state (SelfCheckin or AddGuestDialog shape)
 * @param {object} [opts]
 * @param {boolean} [opts.requireArrival]  — true for host-side (needs arrival_date + nights)
 * @returns {{ ok: boolean, errors: Record<string, string> }}
 */
export function validateAlloggiati(f, opts = {}) {
  const errors = {};
  const requireArrival = !!opts.requireArrival;

  if (!GUEST_TYPES.has(String(f.guest_type || ""))) {
    errors.guest_type = "Tipo alloggiato non valido";
  }

  const lastName = (f.last_name || "").trim();
  if (!lastName) errors.last_name = "Cognome obbligatorio";
  else if (lastName.length > 50) errors.last_name = "Massimo 50 caratteri";

  const firstName = (f.first_name || "").trim();
  if (!firstName) errors.first_name = "Nome obbligatorio";
  else if (firstName.length > 30) errors.first_name = "Massimo 30 caratteri";

  if (f.gender !== "M" && f.gender !== "F") errors.gender = "Sesso richiesto (M/F)";

  const dob = f.date_of_birth || "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
    errors.date_of_birth = "Data di nascita obbligatoria";
  } else {
    const d = new Date(dob);
    const today = new Date();
    if (isNaN(d.getTime())) errors.date_of_birth = "Data non valida";
    else if (d >= today) errors.date_of_birth = "Deve essere nel passato";
    else if (d.getFullYear() < 1900) errors.date_of_birth = "Anno non valido";
  }

  const cob = (f.country_of_birth_code || "").trim();
  if (!cob) errors.country_of_birth_code = "Stato di nascita obbligatorio";
  else if (cob.length > 9) errors.country_of_birth_code = "Codice troppo lungo";

  if (cob === ITALY_COUNTRY_CODE) {
    const comune = (f.comune_of_birth_code || "").trim();
    const prov = (f.province_of_birth || "").trim().toUpperCase();
    if (!comune) errors.comune_of_birth_code = "Obbligatorio per nati in Italia";
    else if (comune.length > 9) errors.comune_of_birth_code = "Codice troppo lungo";
    if (!prov) errors.province_of_birth = "Provincia obbligatoria";
    else if (!PROVINCE_CODES.includes(prov)) errors.province_of_birth = "Sigla provincia non valida";
  }

  const citizenship = (f.citizenship_code || "").trim();
  if (!citizenship) errors.citizenship_code = "Cittadinanza obbligatoria";
  else if (citizenship.length > 9) errors.citizenship_code = "Codice troppo lungo";

  const docType = (f.doc_type || "").trim();
  if (!docType) errors.doc_type = "Tipo documento obbligatorio";
  else if (docType.length > 5) errors.doc_type = "Codice troppo lungo";

  const docNumber = (f.doc_number || "").trim().toUpperCase();
  if (!docNumber) errors.doc_number = "Numero documento obbligatorio";
  else if (!DOC_NUMBER_RE.test(docNumber)) errors.doc_number = "Solo lettere e numeri (4-20)";

  const docPlace = (f.doc_place_code || "").trim();
  if (!docPlace) errors.doc_place_code = "Luogo rilascio obbligatorio";
  else if (docPlace.length > 9) errors.doc_place_code = "Codice troppo lungo";

  if (requireArrival) {
    const arr = f.arrival_date || "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(arr)) errors.arrival_date = "Data arrivo obbligatoria";
    const nights = parseInt(f.nights, 10);
    if (!Number.isFinite(nights) || nights < 1 || nights > 30) errors.nights = "Notti tra 1 e 30";
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Convenience: quick boolean check without the errors object.
 */
export function isAlloggiatiValid(f, opts) {
  return validateAlloggiati(f, opts).ok;
}
